import { useState, useCallback } from "react";
import { apiService } from "../services/apiClient";
import { apiUrl } from "../services/apiUrl";

export const useUserAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // Fetch all users
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.users());
      setUsers(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching users:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user by ID
  const fetchUserById = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.userById(id));
      setCurrentUser(response.data);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching user:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get user details
  const getUserDetails = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(apiUrl.getUserDetails(userId));
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error fetching user details:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update user
  const updateUser = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.put(
        apiUrl.updateUser(),
        userData
      );
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === userData.id ? response.data : u))
      );
      if (currentUser?.id === userData.id) {
        setCurrentUser(response.data);
      }
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error updating user:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Register a new user
  const registerUser = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(apiUrl.aipdRegisterUser(), userData);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      console.error("Error registering user:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Clear current user
  const clearCurrentUser = useCallback(() => {
    setCurrentUser(null);
  }, []);

  return {
    loading,
    error,
    users,
    currentUser,
    fetchUsers,
    fetchUserById,
    getUserDetails,
    updateUser,
    registerUser,
    clearError,
    clearCurrentUser,
  };
};

export default useUserAdmin;
